package team.platforma.multiplatform_material_extension.ui.flexbox

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import team.platforma.multiplatform_material_extension.ui.chip.ExtraInputChip
import team.platforma.multiplatform_material_extension.ui.chip.style.extraInputChipBorder
import team.platforma.multiplatform_material_extension.ui.chip.style.extraInputChipColors
import team.platforma.multiplatform_material_extension.ui.chip.style.extraInputChipElevation
import team.platforma.multiplatform_material_extension.ui.chip.style.extraInputChipHeight
import team.platforma.multiplatform_material_extension.ui.chip.style.extraInputChipLabelTextStyle
import team.platforma.multiplatform_material_extension.ui.chip.style.extraInputChipShape
import team.platforma.multiplatform_material_extension.util.forms.FormProperties
import team.platforma.multiplatform_material_extension.util.forms.rememberForms

@Composable
fun InputChipFlexBox(
    list: List<InputChipItem>,
    onChipClick: (item: InputChipItem) -> Unit,
    minCheckedItems: Int = 3,
    formProperties: FormProperties? = null,
    horizontalItemPadding: Dp = 8.dp,
    verticalItemPadding: Dp = 8.dp,
    flexGravity: FlexGravity = FlexGravity.START,
    isChipClickable: Boolean = true
) {
    var maxWidth by remember { mutableStateOf(0f) }
    val itemWidths = remember { mutableStateListOf<Float>() }


    var checkedMap by remember { mutableStateOf<MutableMap<Long, InputChipItem>>(mutableMapOf()) }


    LaunchedEffect(list) {
        val map: Map<Long, InputChipItem> = list.filter { it.enabled }
            .map { it.id to it }.toMap()

        checkedMap = map.toMutableMap()
        itemWidths.clear()
    }

    val mForms by rememberForms()

    val form = formProperties ?: FormProperties(mForms, key = "")

    LaunchedEffect(form.key) {
        form.form.initValue(form.key, checkedMap.toList().sortedBy { it.first }.toString())
        form.form.changeValue(form.key, checkedMap.toList().sortedBy { it.first }.toString())

        if (checkedMap.size >= minCheckedItems) {
            form.form.setValidateState(form.key, true)
        } else {
            form.form.setValidateState(form.key, false)
        }
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .onGloballyPositioned { coordinates ->
                maxWidth = coordinates.size.width.toFloat()
            }
    )

    // Обновляем размеры элементов
    Column(
        modifier = Modifier.height(0.dp).fillMaxWidth()
    ) {
        list.forEachIndexed { index, item ->
            Box(
                modifier = Modifier
                    .padding(4.dp)
                    .onGloballyPositioned { coordinates ->
                        val width = coordinates.size.width + 40f + verticalItemPadding.value
                        if (index >= itemWidths.size) {
                            itemWidths.add(width)
                        } else {
                            itemWidths[index] = width
                        }
                    }
            ) {
                ExtraInputChip(
                    onClick = {},
                    label = {
                        Text(
                            text = item.title,
                            style = extraInputChipLabelTextStyle()
                        )
                    },
                    selected = false,
                    modifier = Modifier.height(extraInputChipHeight()),
                    colors = extraInputChipColors(),
                    elevation = extraInputChipElevation(),
                    border = extraInputChipBorder(false),
                    shape = extraInputChipShape()
                )
            }
        }
    }

    // Переходим на LaunchedEffect, чтобы следить за изменениями в list и maxWidth
    val rows = rememberUpdatedState(
        if (maxWidth > 0 && itemWidths.isNotEmpty() && itemWidths.size == list.size) {
            mutableListOf<List<InputChipItem>>().apply {
                var currentRow = mutableListOf<InputChipItem>()
                var currentRowWidth = 0f

                list.forEachIndexed { index, item ->
                    val itemWidth = itemWidths[index]
                    if (currentRowWidth + itemWidth > maxWidth) {
                        add(currentRow)
                        currentRow = mutableListOf()
                        currentRowWidth = 0f
                    }
                    currentRow.add(item)
                    currentRowWidth += itemWidth
                }
                if (currentRow.isNotEmpty()) add(currentRow)
            }
        } else {
            emptyList()
        }
    ).value


    // Отображаем строки
    Column(modifier = Modifier.fillMaxWidth()) {
        rows.forEach { row ->
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = when (flexGravity) {
                    FlexGravity.START -> Arrangement.Start
                    FlexGravity.CENTER -> Arrangement.Center
                    FlexGravity.END -> Arrangement.End
                }
            ) {
                row.forEachIndexed { index, item ->
                    Box(
                        modifier = when (index) {
                            0 -> {
                                Modifier
                                    .padding(
                                        start = 0.dp,
                                        end = 0.dp,
                                        top = 0.dp,
                                        bottom = verticalItemPadding
                                    )
                            }

                            else -> {
                                Modifier
                                    .padding(
                                        start = horizontalItemPadding,
                                        end = 0.dp,
                                        top = 0.dp,
                                        bottom = verticalItemPadding
                                    )
                            }
                        }
                    ) {

                        var enabled by remember { mutableStateOf(item.enabled) }

                        ExtraInputChip(
                            onClick = {
                                if (isChipClickable) {
                                    enabled = !enabled
                                    val updateItem = InputChipItem(
                                        id = item.id,
                                        title = item.title,
                                        enabled = enabled
                                    )
                                    if (enabled) {
                                        checkedMap[item.id] = updateItem
                                    } else {
                                        checkedMap -= item.id
                                    }

                                    if (checkedMap.size >= minCheckedItems) {
                                        form.form.setValidateState(form.key, true)
                                    } else {
                                        form.form.setValidateState(form.key, false)
                                    }
                                    form.form.changeValue(
                                        form.key,
                                        checkedMap.toList().sortedBy { it.first }.toString()
                                    )

                                    onChipClick(
                                        updateItem
                                    )
                                }
                            },
                            label = {
                                Text(
                                    text = item.title,
                                    style = extraInputChipLabelTextStyle()
                                )
                            },
                            selected = if(!isChipClickable) false else enabled,
                            modifier = Modifier.height(extraInputChipHeight()),
                            colors = extraInputChipColors(),
                            elevation = extraInputChipElevation(),
                            border = extraInputChipBorder(if(!isChipClickable) false else enabled),
                            shape = extraInputChipShape()
                        )
                    }
                }
            }
        }
    }
}